import React, { useEffect, useRef, useState } from "react";
import "./imageModal.css";

const ImageModal = ({ open, src, alt = "", onClose }) => {
  const [zoomed, setZoomed] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(2);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const imgRef = useRef(null);
  const outerRef = useRef(null);
  const pointersRef = useRef(new Map());

  const dragRef = useRef({
    dragging: false,
    moved: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });

  const pinchRef = useRef({
    active: false,
    startDistance: 0,
    startZoom: 2,
    lastZoom: 2,
    raf: null,
    pendingZoom: null,
  });

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  const getDistance = (p1, p2) =>
    Math.hypot(p1.clientX - p2.clientX, p1.clientY - p2.clientY);

  const getBounds = (zoom = zoomLevel) => {
    const outer = outerRef.current;
    const img = imgRef.current;

    if (!outer || !img) return { maxX: 0, maxY: 0 };

    const scaledWidth = img.clientWidth * zoom;
    const scaledHeight = img.clientHeight * zoom;

    return {
      maxX: Math.max((scaledWidth - outer.clientWidth) / 2, 0),
      maxY: Math.max((scaledHeight - outer.clientHeight) / 2, 0),
    };
  };

  const clampOffset = (nextOffset, zoom = zoomLevel) => {
    const { maxX, maxY } = getBounds(zoom);

    return {
      x: clamp(nextOffset.x, -maxX, maxX),
      y: clamp(nextOffset.y, -maxY, maxY),
    };
  };

  const resetZoom = () => {
    setZoomed(false);
    setZoomLevel(2);
    setOffset({ x: 0, y: 0 });
    pointersRef.current.clear();
    pinchRef.current.active = false;
  };

  const zoomToPoint = (e, nextZoom = 2) => {
    const outer = outerRef.current;
    const img = imgRef.current;

    if (!outer || !img) return;

    const outerRect = outer.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();

    const clickX = e.clientX - imgRect.left;
    const clickY = e.clientY - imgRect.top;

    const percentX = clickX / imgRect.width;
    const percentY = clickY / imgRect.height;

    const scaledWidth = img.clientWidth * nextZoom;
    const scaledHeight = img.clientHeight * nextZoom;

    const rawOffset = {
      x: -((scaledWidth - outerRect.width) * (percentX - 0.5)),
      y: -((scaledHeight - outerRect.height) * (percentY - 0.5)),
    };

    setOffset(clampOffset(rawOffset, nextZoom));
    setZoomLevel(nextZoom);
    setZoomed(true);
  };

  useEffect(() => {
    if (!open) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
      resetZoom();
    };
  }, [open]);

  useEffect(() => {
    const themeMeta = document.querySelector('meta[name="theme-color"]');

    if (!themeMeta) return;

    const originalTheme = themeMeta.getAttribute("content");

    if (open) {
      themeMeta.setAttribute("content", "#000000");
    }

    return () => {
      themeMeta.setAttribute("content", originalTheme || "#ffffff");
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  const handleImageClick = (e) => {
    e.stopPropagation();

    if (dragRef.current.moved || pinchRef.current.active) {
      dragRef.current.moved = false;
      return;
    }

    if (zoomed) {
      resetZoom();
    } else {
      zoomToPoint(e, 2);
    }
  };

  const handleZoomIn = (e) => {
    e.stopPropagation();

    const nextZoom = Math.min(zoomLevel + 0.5, 4);

    setZoomed(true);
    setZoomLevel(nextZoom);
    setOffset((current) => clampOffset(current, nextZoom));
  };

  const handleZoomOut = (e) => {
    e.stopPropagation();

    const nextZoom = Math.max(zoomLevel - 0.5, 1);

    if (nextZoom <= 1) {
      resetZoom();
      return;
    }

    setZoomLevel(nextZoom);
    setOffset((current) => clampOffset(current, nextZoom));
  };

  const handlePointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    e.currentTarget.setPointerCapture(e.pointerId);

    pointersRef.current.set(e.pointerId, {
      clientX: e.clientX,
      clientY: e.clientY,
    });

    const pointers = Array.from(pointersRef.current.values());

    if (pointers.length === 2) {
      const distance = getDistance(pointers[0], pointers[1]);

      pinchRef.current = {
        active: true,
        startDistance: distance,
        startZoom: zoomLevel,
      };

      dragRef.current.dragging = false;
      setIsDragging(false);
      setZoomed(true);
      return;
    }

    if (!zoomed) return;

    dragRef.current = {
      dragging: true,
      moved: false,
      startX: e.clientX,
      startY: e.clientY,
      originX: offset.x,
      originY: offset.y,
    };

    setIsDragging(true);
  };

  const handlePointerMove = (e) => {
    if (!pointersRef.current.has(e.pointerId)) return;

    e.preventDefault();
    e.stopPropagation();

    pointersRef.current.set(e.pointerId, {
      clientX: e.clientX,
      clientY: e.clientY,
    });

    const pointers = Array.from(pointersRef.current.values());

    if (pointers.length === 2 && pinchRef.current.active) {
      const distance = getDistance(pointers[0], pointers[1]);
      if (!pinchRef.current.startDistance) return;
      const scaleChange = distance / pinchRef.current.startDistance;
      let nextZoom = clamp(pinchRef.current.startZoom * scaleChange, 1, 4);
      // Use requestAnimationFrame for smoothness
      pinchRef.current.pendingZoom = nextZoom;
      if (!pinchRef.current.raf) {
        pinchRef.current.raf = requestAnimationFrame(() => {
          setZoomLevel(pinchRef.current.pendingZoom);
          setZoomed(pinchRef.current.pendingZoom > 1.05);
          setOffset((current) =>
            clampOffset(current, pinchRef.current.pendingZoom),
          );
          pinchRef.current.lastZoom = pinchRef.current.pendingZoom;
          pinchRef.current.raf = null;
        });
      }
      dragRef.current.moved = true;
      return;
    }

    if (!dragRef.current.dragging || !zoomed) return;

    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;

    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      dragRef.current.moved = true;
    }

    setOffset(
      clampOffset({
        x: dragRef.current.originX + dx,
        y: dragRef.current.originY + dy,
      }),
    );
  };

  const handlePointerUp = (e) => {
    e.preventDefault();
    e.stopPropagation();

    pointersRef.current.delete(e.pointerId);

    if (dragRef.current.dragging) {
      dragRef.current.dragging = false;
      setIsDragging(false);
    }

    if (pointersRef.current.size < 2) {
      pinchRef.current.active = false;
      if (pinchRef.current.raf) {
        cancelAnimationFrame(pinchRef.current.raf);
        pinchRef.current.raf = null;
      }
    }

    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }

    if (zoomLevel <= 1.05) {
      resetZoom();
    }
  };

  if (!open) return null;

  return (
    <div className="im-modal-overlay" onClick={onClose}>
      <div className="im-modal-content" onClick={(e) => e.stopPropagation()}>
        <button
          className="im-modal-close"
          onClick={onClose}
          aria-label="Close image modal"
        >
          &times;
        </button>

        <div className="im-modal-img-outer" ref={outerRef}>
          <img
            ref={imgRef}
            src={src}
            alt={alt}
            draggable={false}
            onClick={handleImageClick}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            className={`im-modal-img${zoomed ? " zoomed" : ""}${
              isDragging ? " dragging" : ""
            }`}
            style={{
              transform: zoomed
                ? `translate3d(${offset.x}px, ${offset.y}px, 0) scale(${zoomLevel})`
                : "translate3d(0,0,0) scale(1)",
            }}
          />
        </div>

        <div className="im-modal-controls">
          <button
            className="im-modal-zoom-btn"
            onClick={handleZoomIn}
            aria-label="Zoom in"
          >
            +
          </button>

          <button
            className="im-modal-zoom-btn"
            onClick={handleZoomOut}
            aria-label="Zoom out"
          >
            −
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageModal;
